import React, { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { 
  Calendar, 
  Clock, 
  MapPin, 
  AlertTriangle,
  CheckCircle,
  XCircle,
  Loader2,
  ArrowLeft,
  FileText,
  RefreshCw
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const NotaryManageBooking = () => {
  const [searchParams] = useSearchParams();
  const bookingId = searchParams.get('id');
  const token = searchParams.get('token');
  
  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [cancelling, setCancelling] = useState(false);
  const [cancelled, setCancelled] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  
  // Reschedule state
  const [showReschedule, setShowReschedule] = useState(false);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [selectedDate, setSelectedDate] = useState('');
  const [selectedTime, setSelectedTime] = useState('');
  const [rescheduling, setRescheduling] = useState(false);
  const [rescheduled, setRescheduled] = useState(false);
  const [priceInfo, setPriceInfo] = useState(null);
  const [finalizing, setFinalizing] = useState(false);
  const rescheduleSession = searchParams.get('reschedule_session');

  useEffect(() => {
    const fetchBooking = async () => {
      if (!bookingId || !token) {
        setError('Invalid booking link. Please check your email for the correct link.');
        setLoading(false);
        return;
      }

      try {
        const response = await fetch(`${API_URL}/api/notary/booking/${bookingId}/cancel-info?token=${token}`);
        if (!response.ok) {
          const data = await response.json();
          throw new Error(data.detail || 'Booking not found');
        }
        const data = await response.json();
        setBooking(data);
        
        // Set initial selected date to current appointment date
        if (data.appointment_datetime) {
          const apptDate = new Date(data.appointment_datetime);
          setSelectedDate(apptDate.toISOString().split('T')[0]);
        }
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchBooking();
  }, [bookingId, token]);

  const fetchAvailableSlots = async (date) => {
    if (!date) return;
    setLoadingSlots(true);
    setPriceInfo(null);
    try {
      const response = await fetch(
        `${API_URL}/api/notary/booking/${bookingId}/reschedule-availability?token=${token}&date_str=${date}`
      );
      if (response.ok) {
        const data = await response.json();
        setAvailableSlots(data.slots || []);
        setPriceInfo({
          typeName: data.notary_type_name,
          newPrice: data.new_base_price,
          difference: data.price_difference,
          direction: data.direction,
        });
      } else {
        setAvailableSlots([]);
      }
    } catch (err) {
      console.error('Error fetching slots:', err);
      setAvailableSlots([]);
    } finally {
      setLoadingSlots(false);
    }
  };

  // Finalize a reschedule that required an extra payment, after returning from Stripe
  useEffect(() => {
    const finalize = async () => {
      if (!rescheduleSession || !bookingId || !token) return;
      setFinalizing(true);
      try {
        const response = await fetch(
          `${API_URL}/api/notary/booking/${bookingId}/reschedule/finalize?token=${token}`,
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ session_id: rescheduleSession }),
          }
        );
        const data = await response.json();
        if (response.ok && data.success) {
          setRescheduled(true);
          setBooking((prev) => ({ ...prev, appointment_datetime: data.new_appointment_datetime }));
        }
      } catch (err) {
        console.error('Error finalizing reschedule:', err);
      } finally {
        setFinalizing(false);
      }
    };
    finalize();
  }, [rescheduleSession, bookingId, token]);

  useEffect(() => {
    if (showReschedule && selectedDate) {
      fetchAvailableSlots(selectedDate);
    }
  }, [showReschedule, selectedDate]);

  const handleCancel = async () => {
    setCancelling(true);
    try {
      const response = await fetch(
        `${API_URL}/api/notary/booking/${bookingId}/cancel?token=${token}${cancelReason ? `&reason=${encodeURIComponent(cancelReason)}` : ''}`,
        { method: 'POST' }
      );
      
      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || 'Failed to cancel booking');
      }
      
      setCancelled(true);
      setShowCancelConfirm(false);
    } catch (err) {
      alert(err.message);
    } finally {
      setCancelling(false);
    }
  };

  const handleReschedule = async () => {
    if (!selectedDate || !selectedTime) {
      alert('Please select a new date and time');
      return;
    }
    
    setRescheduling(true);
    try {
      const response = await fetch(
        `${API_URL}/api/notary/booking/${bookingId}/reschedule?token=${token}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            new_date: selectedDate,
            new_time: selectedTime,
            origin: window.location.origin
          })
        }
      );
      
      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || 'Failed to reschedule booking');
      }
      
      const data = await response.json();

      // Upgrade to a pricier date — redirect to Stripe to pay the difference
      if (data.requires_payment && data.checkout_url) {
        window.location.href = data.checkout_url;
        return;
      }

      setRescheduled(true);
      setBooking(prev => ({
        ...prev,
        appointment_datetime: data.new_appointment_datetime
      }));
    } catch (err) {
      alert(err.message);
    } finally {
      setRescheduling(false);
    }
  };

  const formatDateTime = (isoString) => {
    if (!isoString) return 'N/A';
    const date = new Date(isoString);
    return date.toLocaleString('en-US', {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      timeZoneName: 'short'
    });
  };

  const getNextSevenDays = () => {
    const days = [];
    const today = new Date();
    for (let i = 0; i < 60; i++) {
      const date = new Date(today);
      date.setDate(today.getDate() + i);
      days.push(date.toISOString().split('T')[0]);
    }
    return days;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-10 h-10 animate-spin text-[#ED1C24] mx-auto mb-4" />
          <p className="text-white">Loading your booking...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center p-4">
        <div className="bg-zinc-900 border border-zinc-700 rounded-2xl p-8 max-w-md w-full text-center">
          <XCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-white mb-2">Unable to Load Booking</h1>
          <p className="text-zinc-400 mb-6">{error}</p>
          <Link
            to="/notary"
            className="inline-flex items-center gap-2 px-6 py-3 bg-[#ED1C24] text-white font-medium rounded-lg hover:bg-red-700 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Book New Appointment
          </Link>
        </div>
      </div>
    );
  }

  if (cancelled) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center p-4">
        <div className="bg-zinc-900 border border-zinc-700 rounded-2xl p-8 max-w-md w-full text-center">
          <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-white mb-2">Booking Cancelled</h1>
          <p className="text-zinc-400 mb-4">
            Your appointment has been successfully cancelled.
          </p>
          <p className="text-sm text-amber-400 mb-6">
            If your cancellation qualifies for a refund, our team will review and process it manually. Refunds aren't guaranteed for late cancellations.
          </p>
          <Link
            to="/notary"
            className="inline-flex items-center gap-2 px-6 py-3 bg-[#ED1C24] text-white font-medium rounded-lg hover:bg-red-700 transition-colors"
          >
            Book New Appointment
          </Link>
        </div>
      </div>
    );
  }

  if (rescheduled) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center p-4">
        <div className="bg-zinc-900 border border-zinc-700 rounded-2xl p-8 max-w-md w-full text-center">
          <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-white mb-2">Booking Rescheduled</h1>
          <p className="text-zinc-400 mb-2">
            Your appointment has been successfully rescheduled.
          </p>
          <p className="text-white font-semibold mb-4">
            New Time: {formatDateTime(booking?.appointment_datetime)}
          </p>
          <p className="text-sm text-zinc-500 mb-6">
            A confirmation email has been sent with your updated appointment details.
          </p>
          <Link
            to="/notary"
            className="inline-flex items-center gap-2 px-6 py-3 bg-zinc-700 text-white font-medium rounded-lg hover:bg-zinc-600 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </Link>
        </div>
      </div>
    );
  }

  const statusColors = {
    confirmed: { bg: 'bg-green-500/20', text: 'text-green-400', icon: CheckCircle },
    cancelled: { bg: 'bg-red-500/20', text: 'text-red-400', icon: XCircle },
    completed: { bg: 'bg-blue-500/20', text: 'text-blue-400', icon: CheckCircle },
    pending: { bg: 'bg-yellow-500/20', text: 'text-yellow-400', icon: Clock }
  };
  
  const status = statusColors[booking?.status] || statusColors.pending;
  const StatusIcon = status.icon;
  
  // Can modify (cancel/reschedule) only if refundable and within window
  const canModify = booking?.refundable && booking?.can_cancel && booking?.status === 'confirmed';

  return (
    <div className="min-h-screen bg-black">
      {/* Header */}
      <div className="bg-zinc-900 border-b border-zinc-700 py-6 px-4">
        <div className="max-w-2xl mx-auto">
          <Link to="/notary" className="text-zinc-400 hover:text-white text-sm flex items-center gap-1 mb-4">
            <ArrowLeft className="w-4 h-4" /> Back to Booking
          </Link>
          <h1 className="text-2xl font-bold text-white">Manage Your Booking</h1>
          <p className="text-zinc-400 mt-1">Confirmation #{booking?.id}</p>
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-4 py-8">
        {/* Status Badge */}
        <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-full ${status.bg} ${status.text} mb-6`}>
          <StatusIcon className="w-5 h-5" />
          <span className="font-semibold capitalize">{booking?.status}</span>
        </div>

        {/* Booking Details Card */}
        <div className="bg-zinc-900 border border-zinc-700 rounded-2xl overflow-hidden mb-6">
          <div className="p-6 space-y-4">
            <div className="flex items-start gap-4">
              <div className="p-2 bg-[#ED1C24]/20 rounded-lg">
                <FileText className="w-5 h-5 text-[#ED1C24]" />
              </div>
              <div>
                <p className="text-sm text-zinc-400">Service</p>
                <p className="font-semibold text-white">{booking?.notary_type_name}</p>
              </div>
            </div>

            <div className="flex items-start gap-4">
              <div className="p-2 bg-zinc-700 rounded-lg">
                <Calendar className="w-5 h-5 text-zinc-300" />
              </div>
              <div>
                <p className="text-sm text-zinc-400">Date & Time</p>
                <p className="font-semibold text-white">{formatDateTime(booking?.appointment_datetime)}</p>
              </div>
            </div>

            <div className="flex items-start gap-4">
              <div className="p-2 bg-zinc-700 rounded-lg">
                <MapPin className="w-5 h-5 text-zinc-300" />
              </div>
              <div>
                <p className="text-sm text-zinc-400">Location</p>
                <p className="font-semibold text-white">{booking?.address}</p>
              </div>
            </div>
          </div>

          {/* Payment Summary */}
          <div className="border-t border-zinc-700 p-6">
            <h3 className="text-sm font-semibold text-zinc-400 uppercase tracking-wider mb-3">Payment Summary</h3>
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-zinc-400">Service Fee</span>
                <span className="text-white">${booking?.base_price?.toFixed(2)}</span>
              </div>
              {booking?.mileage_fee > 0 && (
                <div className="flex justify-between text-sm">
                  <span className="text-zinc-400">Mileage Fee</span>
                  <span className="text-white">${booking?.mileage_fee?.toFixed(2)}</span>
                </div>
              )}
              <div className="flex justify-between pt-2 border-t border-zinc-700">
                <span className="font-semibold text-white">Total Paid</span>
                <span className="font-bold text-green-400">${booking?.total_price?.toFixed(2)}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Cancel/Reschedule Section - Only for refundable bookings within window */}
        {booking?.status === 'confirmed' && canModify && (
          <div className="bg-zinc-900 border border-zinc-700 rounded-2xl p-6 space-y-4">
            <h3 className="text-lg font-semibold text-white">Need to Make Changes?</h3>
            <p className="text-zinc-400 text-sm">
              You can cancel or reschedule up to {booking?.cancellation_deadline_hours} hours before your appointment.
            </p>
            
            {!showCancelConfirm && !showReschedule && (
              <div className="flex gap-3">
                <button
                  onClick={() => setShowReschedule(true)}
                  className="flex-1 px-4 py-2 bg-zinc-700 text-white font-medium rounded-lg hover:bg-zinc-600 transition-colors flex items-center justify-center gap-2"
                >
                  <RefreshCw className="w-4 h-4" />
                  Reschedule
                </button>
                <button
                  onClick={() => setShowCancelConfirm(true)}
                  className="flex-1 px-4 py-2 bg-red-600 text-white font-medium rounded-lg hover:bg-red-700 transition-colors"
                >
                  Cancel Booking
                </button>
              </div>
            )}

            {/* Reschedule UI */}
            {showReschedule && (
              <div className="bg-zinc-800 border border-zinc-600 rounded-xl p-4 space-y-4">
                <div className="flex items-center justify-between">
                  <h4 className="font-medium text-white">Select New Date & Time</h4>
                  <button
                    onClick={() => setShowReschedule(false)}
                    className="text-zinc-400 hover:text-white text-sm"
                  >
                    Cancel
                  </button>
                </div>
                
                <div>
                  <label className="block text-sm text-zinc-400 mb-1">Date</label>
                  <select
                    value={selectedDate}
                    onChange={(e) => {
                      setSelectedDate(e.target.value);
                      setSelectedTime('');
                    }}
                    className="w-full px-3 py-2 bg-zinc-700 border border-zinc-600 rounded-lg text-white focus:ring-2 focus:ring-[#ED1C24] focus:outline-none"
                  >
                    <option value="">Select a date</option>
                    {getNextSevenDays().map(date => (
                      <option key={date} value={date}>
                        {new Date(date + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
                      </option>
                    ))}
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm text-zinc-400 mb-1">Time</label>
                  {loadingSlots ? (
                    <div className="flex items-center gap-2 text-zinc-400 py-2">
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Loading available times...
                    </div>
                  ) : availableSlots.length === 0 ? (
                    <p className="text-zinc-500 text-sm py-2">No available times for this date</p>
                  ) : (
                    <div className="grid grid-cols-4 gap-2 max-h-40 overflow-y-auto">
                      {availableSlots.map(slot => (
                        <button
                          key={slot.time}
                          data-testid={`reschedule-slot-${slot.time}`}
                          onClick={() => setSelectedTime(slot.time)}
                          className={`px-2 py-1.5 text-sm rounded-lg transition-colors ${
                            selectedTime === slot.time
                              ? 'bg-[#ED1C24] text-white'
                              : 'bg-zinc-700 text-zinc-300 hover:bg-zinc-600'
                          }`}
                        >
                          {slot.display_time}
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                {/* Dynamic price notice based on the selected date's service tier */}
                {priceInfo && !loadingSlots && availableSlots.length > 0 && (
                  <div
                    data-testid="reschedule-price-notice"
                    className={`rounded-lg p-3 text-sm border ${
                      priceInfo.direction === 'charge'
                        ? 'bg-amber-500/10 border-amber-500/30 text-amber-300'
                        : priceInfo.direction === 'refund'
                        ? 'bg-green-500/10 border-green-500/30 text-green-300'
                        : 'bg-zinc-700/40 border-zinc-600 text-zinc-300'
                    }`}
                  >
                    <p className="font-medium text-white">{priceInfo.typeName} — ${priceInfo.newPrice?.toFixed(2)}</p>
                    {priceInfo.direction === 'charge' && (
                      <p className="mt-1">This date costs ${Math.abs(priceInfo.difference).toFixed(2)} more. You'll be taken to secure payment to pay the difference before your new time is confirmed.</p>
                    )}
                    {priceInfo.direction === 'refund' && (
                      <p className="mt-1">This date is cheaper — you'll be automatically refunded ${Math.abs(priceInfo.difference).toFixed(2)} to your original payment method (5-10 business days).</p>
                    )}
                    {priceInfo.direction === 'same' && (
                      <p className="mt-1">Same price as your current booking — no additional charge.</p>
                    )}
                  </div>
                )}
                
                <button
                  onClick={handleReschedule}
                  disabled={!selectedDate || !selectedTime || rescheduling}
                  data-testid="confirm-reschedule-button"
                  className="w-full px-4 py-2 bg-[#ED1C24] text-white font-medium rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {rescheduling ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      {priceInfo?.direction === 'charge' ? 'Redirecting to payment...' : 'Rescheduling...'}
                    </>
                  ) : (
                    priceInfo?.direction === 'charge' ? `Pay $${Math.abs(priceInfo.difference).toFixed(2)} & Reschedule` : 'Confirm Reschedule'
                  )}
                </button>
              </div>
            )}

            {/* Cancel Confirmation */}
            {showCancelConfirm && (
              <div className="bg-zinc-800 border border-zinc-600 rounded-xl p-4 space-y-4">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-medium text-white">Are you sure you want to cancel?</p>
                    <p className="text-sm text-zinc-400 mt-1">
                      Any eligible refund is handled manually by our team and isn't guaranteed for late cancellations.
                    </p>
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm text-zinc-400 mb-1">Reason (optional)</label>
                  <input
                    type="text"
                    value={cancelReason}
                    onChange={(e) => setCancelReason(e.target.value)}
                    placeholder="Let us know why you're cancelling..."
                    className="w-full px-3 py-2 bg-zinc-700 border border-zinc-600 rounded-lg text-white placeholder-zinc-400 focus:ring-2 focus:ring-[#ED1C24] focus:outline-none"
                  />
                </div>
                
                <div className="flex gap-3">
                  <button
                    onClick={() => setShowCancelConfirm(false)}
                    className="flex-1 px-4 py-2 bg-zinc-700 text-white font-medium rounded-lg hover:bg-zinc-600 transition-colors"
                  >
                    Keep Appointment
                  </button>
                  <button
                    onClick={handleCancel}
                    disabled={cancelling}
                    className="flex-1 px-4 py-2 bg-red-600 text-white font-medium rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                  >
                    {cancelling ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        Cancelling...
                      </>
                    ) : (
                      'Yes, Cancel'
                    )}
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Window Passed - Only show for refundable bookings */}
        {booking?.status === 'confirmed' && booking?.refundable && !booking?.can_cancel && (
          <div className="bg-zinc-900 border border-zinc-700 rounded-2xl p-6">
            <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-medium text-amber-400">Modification window has passed</p>
                  <p className="text-sm text-amber-300/80 mt-1">
                    Changes must be made at least {booking?.cancellation_deadline_hours} hours before your appointment.
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Already Cancelled */}
        {booking?.status === 'cancelled' && (
          <div className="bg-zinc-900 border border-zinc-700 rounded-2xl p-6 text-center">
            <XCircle className="w-12 h-12 text-red-400 mx-auto mb-3" />
            <h3 className="text-lg font-semibold text-white mb-2">This booking has been cancelled</h3>
            <p className="text-zinc-400 text-sm mb-4">
              {booking?.refundable 
                ? "Any eligible refund is handled manually by our team."
                : ""}
            </p>
            <Link
              to="/notary"
              className="inline-flex items-center gap-2 px-6 py-2 bg-[#ED1C24] text-white font-medium rounded-lg hover:bg-red-700 transition-colors"
            >
              Book New Appointment
            </Link>
          </div>
        )}

        {/* Completed */}
        {booking?.status === 'completed' && (
          <div className="bg-zinc-900 border border-zinc-700 rounded-2xl p-6 text-center">
            <CheckCircle className="w-12 h-12 text-green-400 mx-auto mb-3" />
            <h3 className="text-lg font-semibold text-white mb-2">Appointment Completed</h3>
            <p className="text-zinc-400 text-sm mb-4">
              Thank you for choosing WesTex Legal Mobile Notary!
            </p>
            <a
              href="https://g.page/r/CRKnArbK9f3VEAE/review"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-6 py-2 bg-[#ED1C24] text-white font-medium rounded-lg hover:bg-red-700 transition-colors"
            >
              Leave a Review
            </a>
          </div>
        )}

        {/* Contact Info */}
        <div className="mt-6 text-center">
          <p className="text-zinc-400 text-sm">Questions? Contact us:</p>
          <p className="text-white">
            <a href="tel:8064011749" className="hover:text-[#ED1C24]">(806) 401-1749</a>
            {' • '}
            <a href="mailto:WesTexLegal@gmail.com" className="hover:text-[#ED1C24]">WesTexLegal@gmail.com</a>
          </p>
        </div>
      </div>
    </div>
  );
};

export default NotaryManageBooking;
